import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  CreditCard,
  Clock,
  AlertCircle,
  ArrowUpRight,
  RefreshCw,
  Calendar,
  Coins,
  Repeat,
} from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { useTestClock } from "@/contexts/TestClockContext";
import { WelcomeCreditsCard } from "@/components/settings/WelcomeCreditsCard";
import { useCreditSystem } from "@/hooks/useCreditSystem";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatInTimeZone } from "date-fns-tz";

export function SubscriptionSection() {
  const { subscription, isLoading, refresh } = useSubscription();
  const { stats } = useCreditSystem();
  const { isTestMode, openCheckoutInNewTab, getCurrentTime } = useTestClock();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-6 text-sm text-muted-foreground animate-pulse">
        Loading subscription…
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="rounded-2xl border border-dashed border-border/80 bg-card p-10 text-center">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
          <CreditCard className="h-7 w-7 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-lg mb-1">No subscription yet</h3>
        <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">
          Choose a plan to unlock the full Email2Contact experience and start extracting contacts at scale.
        </p>
        <Button asChild size="lg">
          <Link to="/plans" className="gap-2">
            Browse plans <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    );
  }

  // Welcome tier → dedicated card
  if (subscription.tier === "welcome") {
    return (
      <div className="space-y-4">
        <WelcomeCreditsCard
          creditsRemaining={stats.currentBalance ?? subscription.credits_remaining}
          creditsMonthly={subscription.credits_monthly}
        />
        <div className="rounded-xl border border-border/60 bg-gradient-to-br from-primary/5 to-transparent p-6">
          <h3 className="font-semibold mb-1">Ready to upgrade?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Pick a plan to get monthly credits, top-ups, and access to all advanced features.
          </p>
          <Button asChild>
            <Link to="/plans" className="gap-2">
              View plans <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.error) {
        toast({ title: "Error", description: data.message || "Could not open subscription management", variant: "destructive" });
        return;
      }
      if (data?.url) {
        if (openCheckoutInNewTab) window.open(data.url, "_blank");
        else window.location.href = data.url;
      }
    } catch {
      toast({ title: "Error", description: "Could not open subscription management.", variant: "destructive" });
    }
  };

  const handleRefresh = async () => {
    try {
      await supabase.functions.invoke("check-subscription");
      await refresh();
      toast({ title: "Subscription refreshed" });
    } catch {
      toast({ title: "Refresh failed", variant: "destructive" });
    }
  };

  const renewalDate = new Date(subscription.current_period_end);
  const now = getCurrentTime();
  const daysUntilRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const renewalLabel =
    daysUntilRenewal > 0
      ? `in ${daysUntilRenewal} ${daysUntilRenewal === 1 ? "day" : "days"}`
      : daysUntilRenewal === 0
      ? "today"
      : `${Math.abs(daysUntilRenewal)} ${Math.abs(daysUntilRenewal) === 1 ? "day" : "days"} ago`;

  const status: { label: string; tone: "active" | "cancelling" | "cancelled" | "debug" } =
    subscription.status === "cancelled" && !subscription.cancel_at_period_end
      ? { label: "Cancelled", tone: "cancelled" }
      : subscription.cancel_at_period_end
      ? { label: "Cancelling soon", tone: "cancelling" }
      : subscription.tier === "debug"
      ? { label: "Debug mode", tone: "debug" }
      : { label: "Active", tone: "active" };

  const toneStyles = {
    active: "bg-success/10 text-success border-success/30",
    cancelling: "bg-warning/10 text-warning border-warning/30",
    cancelled: "bg-destructive/10 text-destructive border-destructive/30",
    debug: "bg-orange/10 text-orange border-orange/30",
  }[status.tone];

  const statusDot = {
    active: "bg-success",
    cancelling: "bg-warning",
    cancelled: "bg-destructive",
    debug: "bg-orange",
  }[status.tone];

  const cycleLabel =
    subscription.billing_cycle === "annual"
      ? "Annual"
      : subscription.billing_cycle === "daily"
      ? "Daily"
      : "Monthly";

  return (
    <div className="space-y-5">
      {/* Plan hero */}
      <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-primary/8 via-card to-card p-6 sm:p-7">
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Current plan</p>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-3xl sm:text-4xl font-semibold capitalize tracking-tight">{subscription.tier}</h2>
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${toneStyles}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${statusDot} ${status.tone === "active" ? "animate-pulse" : ""}`} />
                {status.label}
              </span>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Badge variant="secondary" className="gap-1.5">
                <Repeat className="h-3 w-3" />
                {cycleLabel} billing
              </Badge>
              <Badge variant="outline" className="gap-1.5">
                <Coins className="h-3 w-3" />
                {subscription.credits_monthly.toLocaleString()} credits / cycle
              </Badge>
            </div>
          </div>

          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleRefresh} className="gap-2">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
            <Button size="sm" onClick={handleManageSubscription} className="gap-2 shadow-fluent-2">
              <CreditCard className="h-3.5 w-3.5" /> Manage
            </Button>
          </div>
        </div>
      </div>

      {/* Status detail */}
      {status.tone === "active" && (
        <div className="rounded-xl border border-success/20 bg-success/5 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
              <Clock className="h-4 w-4 text-success" />
            </div>
            <div className="text-sm flex-1">
              <p className="font-medium">
                Next renewal <span className="font-semibold">{renewalLabel}</span>
                {isTestMode && (
                  <span className="text-xs text-primary ml-1.5 font-normal">
                    ({formatInTimeZone(renewalDate, Intl.DateTimeFormat().resolvedOptions().timeZone, "PPP")})
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Your {subscription.credits_monthly.toLocaleString()} credits will be automatically renewed.
              </p>
            </div>
          </div>
        </div>
      )}

      {status.tone === "cancelling" && (
        <Alert className="border-warning/40 bg-warning/5">
          <AlertCircle className="h-4 w-4 text-warning" />
          <AlertDescription>
            <p className="font-medium text-foreground mb-1">Subscription scheduled for cancellation</p>
            <p className="text-sm text-muted-foreground">
              Active until{" "}
              <span className="font-semibold text-foreground">
                {formatInTimeZone(renewalDate, Intl.DateTimeFormat().resolvedOptions().timeZone, "PPP")}
              </span>
              . All credits ({subscription.credits_remaining.toLocaleString()}) will expire on this date.
            </p>
            <button
              onClick={handleManageSubscription}
              className="text-xs underline mt-2 text-warning font-medium hover:no-underline"
            >
              Reactivate subscription
            </button>
          </AlertDescription>
        </Alert>
      )}

      {status.tone === "cancelled" && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium mb-1">Subscription cancelled</p>
            <p className="text-sm">
              {renewalDate > new Date() ? (
                <>
                  Ends on{" "}
                  <span className="font-semibold">
                    {formatInTimeZone(renewalDate, Intl.DateTimeFormat().resolvedOptions().timeZone, "PPP")}
                  </span>
                  .
                </>
              ) : (
                <>
                  Ended on{" "}
                  <span className="font-semibold">
                    {formatInTimeZone(renewalDate, Intl.DateTimeFormat().resolvedOptions().timeZone, "PPP")}
                  </span>
                  .
                </>
              )}
            </p>
            <Button asChild size="sm" className="mt-3">
              <Link to="/plans">Subscribe again</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Plan change CTA */}
      <div className="rounded-xl border border-border/60 bg-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm">Need a different plan?</h3>
              <p className="text-sm text-muted-foreground mt-0.5">Compare plans, change billing cycle, or cancel anytime.</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" asChild>
              <Link to="/pricing">View pricing</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/support">Contact support</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
